import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, CircularProgress, IconButton } from '@mui/material';
import { DataGridPremium, GridColDef, GridRenderCellParams, itIT } from '@mui/x-data-grid-premium';
import { EnhancedAccordion } from 'components/enhanced/accordion/EnhancedAccordion';
import { AutocompleteAdapter, EnhancedForm, IdentityField } from 'components/enhanced/form';
import { required } from 'components/helpers/validators';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OptionDescription } from 'shared/types/general';
import { ITabTableLayerRow } from './types';
import { normalizeRows } from './TabUtils';

interface ITabTableLayerParams {
  title: string;
  addElementLabel: string;
  selectElementLabel: string;
  selectedElements?: Array<number | OptionDescription>;
  options: OptionDescription[];
  disableEdit?: boolean;
  isLoading?: boolean;
  optionLabel?: string;
  onSubmit: (elementIds: number[]) => void;
}

function TabTableLayer(params: ITabTableLayerParams) {
  const { t } = useTranslation();
  const {
    title,
    addElementLabel,
    selectElementLabel,
    selectedElements,
    options,
    disableEdit,
    isLoading,
    optionLabel = 'description',
    onSubmit
  } = params;

  const [rows, setRows] = useState<ITabTableLayerRow[]>([]);
  const formId = `add-tab-layer-${title.replace(/\s+/g, '-').toLowerCase()}`;

  useEffect(() => {
    setRows(normalizeRows(selectedElements, options));
  }, [selectedElements, options]);

  const submitRows = (nextRows: ITabTableLayerRow[]) => {
    const nextIds = nextRows.map((row) => Number(row.id)).filter((id) => !Number.isNaN(id));
    onSubmit(nextIds);
    setRows(nextRows);
  };

  const handleDelete = (id: number | string) => {
    const nextRows = rows.filter((row) => row.id !== id);
    submitRows(nextRows);
  };

  const handleAddElement = (values: { element: number }) => {
    const selectedId = Number(values?.element);
    const selectedOption = options.find((opt) => Number(opt.id) === selectedId);

    if (!selectedOption) {
      return;
    }

    const alreadyPresent = rows.some((row) => Number(row.id) === selectedId);
    if (alreadyPresent) {
      return;
    }

    const nextRows = [
      ...rows,
      {
        id: selectedId,
        code: selectedOption.code,
        description: selectedOption.description
      }
    ];

    submitRows(nextRows);
  };

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: 'code',
        headerName: t('global.code'),
        flex: 1,
        minWidth: 150
      },
      {
        field: 'description',
        headerName: t('global.description'),
        flex: 2,
        minWidth: 240
      }
    ];

    if (disableEdit) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (cellParams: GridRenderCellParams) => (
          <IconButton onClick={() => handleDelete(cellParams.row.id)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )
      }
    ];
  }, [disableEdit, rows, t]);

  return (
    <div>
      {!disableEdit && (
        <EnhancedAccordion title={title} subtitle={addElementLabel}>
          <EnhancedForm
            onSubmit={handleAddElement}
            formId={formId}
            costumControls={
              <Button type="submit" form={formId} variant="outlined" className="w-full">
                {t('global.add')}
              </Button>
            }>
            <IdentityField
              name="element"
              component={AutocompleteAdapter}
              options={options}
              optionLabel={optionLabel}
              label={selectElementLabel}
              type="text"
              validate={required}
              required
            />
          </EnhancedForm>
        </EnhancedAccordion>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <div style={{ height: 600, width: '100%' }}>
          <DataGridPremium
            density="compact"
            rows={rows}
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
            sx={{ border: 'none' }}
            columns={columns}
            pageSizeOptions={[5, 10, 20, 50, 100]}
          />
        </div>
      )}
    </div>
  );
}

export default TabTableLayer;
